import React, { useState, useCallback, useRef } from 'react';

import PropTypes from 'prop-types';
import { FormattedMessage, defineMessages } from 'react-intl';
import { Link } from 'react-router-dom';

import { Button } from 'ui-components/components/button';
import { Popover } from 'ui-components/components/popover';

import { getCarriersPath } from '../../routes/constants';
import { popoverBodyStyles, popoverTipShapeStylesBelow } from './apply-button-popover.styles';

const messages = defineMessages({
  enableService: {
    description: 'lets the user know why they cannot apply a service',
    defaultMessage: 'To take advantage of these savings, enable the {serviceName} service via <link>Settings > Carriers & Services</link>.',
    id: 'Yf3QrQ',
  },
});

const PopoverContent = ({ serviceName }) => {
  return (
    <div>
      <FormattedMessage
        {...messages.enableService}
        values={{
          serviceName,
          link: (chunks) => <Link to={getCarriersPath()}>{chunks}</Link>,
        }}
      />
    </div>
  );
};
PopoverContent.propTypes = {
  serviceName: PropTypes.string.isRequired,
};

const Y_PADDING_FOR_DIRECTION_SELECTION = 60;
const ApplyButton = ({ service, onClick, amplitudeTrackingId = '', children, theme = 'link', buttonClassName }) => {
  const { isHidden } = service;
  const [isOpen, setIsOpen] = useState(false);
  const closePopover = useCallback(() => {
    setIsOpen(false);
  }, [setIsOpen]);

  const popoverWrapperRef = useRef(null);
  const targetRef = useRef(null);

  const openPopover = useCallback(() => {
    if (isHidden) {
      setIsOpen(true);
    }
  }, [setIsOpen, isHidden]);

  const shouldUseBelow =
    targetRef.current && document.body.clientHeight - targetRef.current.getBoundingClientRect().y >= Y_PADDING_FOR_DIRECTION_SELECTION;

  const popoverProps = {
    isOpen,
    body: <PopoverContent serviceName={service.name} />,
    ref: popoverWrapperRef,
    onOuterAction: closePopover,
    place: shouldUseBelow ? 'below' : 'above',
    appendTarget: document.querySelector('#app-root'),
    enterExitTransitionDurationMs: 0,
    popoverBodyStyles,
    popoverTipShapeStyles: shouldUseBelow ? popoverTipShapeStylesBelow : '',
    tipSize: 15,
  };
  if (targetRef?.current) {
    popoverProps.target = targetRef.current;
  }
  return (
    <Popover {...popoverProps}>
      <span ref={targetRef} onMouseEnter={openPopover}>
        <Button
          className={buttonClassName}
          disabled={service.isHidden}
          onClick={onClick}
          amplitudeTrackingId={amplitudeTrackingId}
          theme={theme}
        >
          {children}
        </Button>
      </span>
    </Popover>
  );
};

ApplyButton.propTypes = {
  service: PropTypes.shape({
    name: PropTypes.string.isRequired,
    serviceId: PropTypes.string.isRequired,
    isHidden: PropTypes.bool.isRequired,
  }).isRequired,
  onClick: PropTypes.func.isRequired,
  amplitudeTrackingId: PropTypes.string,
  children: PropTypes.node.isRequired,
  theme: PropTypes.string,
  buttonClassName: PropTypes.string,
};

export default ApplyButton;
